import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiPlus, FiPlay, FiBookOpen, FiCpu, FiDollarSign, FiFile, FiFolder, FiRefreshCw, FiActivity, FiZap, FiDatabase, FiTrendingUp } from 'react-icons/fi';
import { encodeNotebookPath } from '../components/workspace/StrategyTemplatePicker';
import { HiOutlineSparkles, HiOutlineChip } from 'react-icons/hi';
import { BiRocket } from 'react-icons/bi';
import { MdCheckCircle } from 'react-icons/md';
import { SiPytorch, SiJupyter } from 'react-icons/si';
import NotebookLauncher from '../components/notebooks/NotebookLauncher';
import { getApiUrl } from '../utils/api';

interface AvailableInstance {
  instance_id: string;
  provider: string;
  instance_type: string;
  region: string;
  gpu_type: string;
  launch_time: string;
  hourly_cost: number;
  has_notebook: boolean;
  project_name: string;
  public_ip: string;
  status: string;
}

interface NotebookFile {
  id?: string;
  name: string;
  path: string;
  last_modified: string;
  size: number;
  description?: string;
  category?: string;
  is_sample?: boolean;
  // Optional friendly name when the backend has more context than the raw
  // filename provides — e.g. research-feed starter notebooks use the source
  // paper title instead of the UUID-based S3 key basename.
  display_name?: string;
  research_insight_id?: string;
}

/**
 * Render-friendly name for a notebook. Prefers the backend-provided
 * `display_name`, falls back to the basename with `.ipynb` stripped and
 * underscores spaced out. Keeps all three call sites in NotebooksPage in
 * sync.
 */
function notebookDisplayName(nb: NotebookFile): string {
  if (nb.display_name) return nb.display_name;
  return nb.name.replace(/\.ipynb$/, '').replace(/_/g, ' ');
}

export default function NotebooksPage() {
  const [availableInstances, setAvailableInstances] = useState<AvailableInstance[]>([]);
  const [userNotebooks, setUserNotebooks] = useState<NotebookFile[]>([]);
  const [sampleNotebooks, setSampleNotebooks] = useState<NotebookFile[]>([]);
  const [showNotebookLauncher, setShowNotebookLauncher] = useState(false);
  const [preSelectedInstanceId, setPreSelectedInstanceId] = useState<string | null>(null);
  const [showUserNotebooksModal, setShowUserNotebooksModal] = useState(false);
  const [selectedSampleNotebook, setSelectedSampleNotebook] = useState<NotebookFile | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [pendingTemplate, setPendingTemplate] = useState<string | null>(null);
  const [notebookError, setNotebookError] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }

    fetchAvailableInstances();
    fetchNotebooks();

    // Check for template parameter from URL
    const templatePath = searchParams.get('template');
    if (templatePath) {
      setPendingTemplate(templatePath);
    }

    // Check for template in sessionStorage (from ComputePromptModal)
    const storedTemplate = sessionStorage.getItem('strategy_template');
    if (storedTemplate) {
      try {
        const { templatePath: storedPath } = JSON.parse(storedTemplate);
        if (storedPath) {
          setPendingTemplate(storedPath);
        }
        // Clear after reading
        sessionStorage.removeItem('strategy_template');
      } catch (e) {
        console.error('Error parsing stored template:', e);
      }
    }
  }, [navigate, searchParams]);

  // Effect to auto-open template when we have a pending template and running instance
  useEffect(() => {
    if (pendingTemplate && availableInstances.length > 0) {
      const runningInstance = availableInstances.find(i => i.has_notebook);
      if (runningInstance) {
        // Auto-open the template notebook
        launchNotebook(runningInstance.instance_id, pendingTemplate);
        setPendingTemplate(null);
      }
    }
  }, [pendingTemplate, availableInstances]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchNotebooks = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${getApiUrl()}/api/v1/storage/notebooks/list`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Fetched notebooks data:', data);
        setUserNotebooks(data.user_notebooks || []);
        // Strategy templates live in the Template Library page; the
        // Notebooks page surfaces only ready-to-run sample notebooks.
        setSampleNotebooks(data.sample_notebooks || []);
        setNotebookError(false);
      } else {
        console.warn('Failed to fetch notebooks');
        setSampleNotebooks([]);
        setNotebookError(true);
      }
    } catch (error) {
      console.error('Error fetching notebooks:', error);
      setSampleNotebooks([]);
      setNotebookError(true);
    }
  };

  const fetchAvailableInstances = async () => {
    setRefreshing(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${getApiUrl()}/api/v1/notebooks/available-instances`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableInstances(data);
      }
    } catch (err) {
      console.error('Error fetching instances:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const launchNotebook = (instanceId: string, notebookPath?: string) => {
    try {
      sessionStorage.setItem('notebook_launch_params', JSON.stringify({
        instance_id: instanceId,
        notebook_path: notebookPath
      }));
      navigate(`/dashboard/notebook/new-session`);
    } catch (err: any) {
      console.error('Error launching notebook:', err);
    }
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getCategoryIcon = (category?: string) => {
    switch (category) {
      case 'Getting Started': return <SiJupyter className="w-5 h-5 text-green-400" />;
      case 'System': return <HiOutlineChip className="w-5 h-5 text-blue-400" />;
      case 'Machine Learning': return <SiPytorch className="w-5 h-5 text-purple-400" />;
      case 'Data Processing': return <FiDatabase className="w-5 h-5 text-orange-400" />;
      case 'Finance': return <FiTrendingUp className="w-5 h-5 text-yellow-400" />;
      case 'SDK': return <FiZap className="w-5 h-5 text-indigo-400" />;
      default: return <FiFile className="w-5 h-5 text-gray-400" />;
    }
  };

  const runningInstances = availableInstances.filter(i => i.has_notebook);

  return (
    <div className="min-h-screen bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white">Notebooks</h1>
              <p className="mt-2 text-gray-400">
                Launch Jupyter notebooks on GPU-powered instances
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  fetchAvailableInstances();
                  fetchNotebooks();
                }}
                disabled={refreshing}
                className="p-3 text-gray-400 hover:text-white transition-colors"
                title="Refresh"
              >
                <FiRefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={() => setShowNotebookLauncher(true)}
                className="px-6 py-3 bg-white text-black font-medium rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-2"
              >
                <FiPlus className="w-5 h-5" />
                Launch New Notebook
              </button>
            </div>
          </div>
        </div>

        {/* Active Sessions Section - Always visible */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <FiActivity className="w-5 h-5 text-green-500" />
            Active Notebook Sessions
          </h2>
          
          {runningInstances.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {runningInstances.map((instance) => (
                <div
                  key={instance.instance_id}
                  className="bg-gray-900 border border-gray-800 rounded-lg p-4 hover:border-gray-700 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-medium text-white">
                        {instance.project_name || instance.instance_type}
                      </p>
                      <p className="text-sm text-gray-400 flex items-center gap-2 mt-1">
                        <FiCpu className="w-3 h-3" />
                        {instance.gpu_type} • {instance.region}
                      </p>
                    </div>
                    <span className="text-green-500 text-xs bg-green-900/30 px-2 py-1 rounded flex items-center gap-1">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                      Running
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500 flex items-center gap-1">
                      <FiDollarSign className="w-3 h-3" />
                      {(instance.hourly_cost ?? 0).toFixed(2)}/hr
                    </span>
                    <button
                      onClick={() => {
                        sessionStorage.setItem('notebook_launch_params', JSON.stringify({
                          instance_id: instance.instance_id
                        }));
                        navigate(`/dashboard/notebook/new-session`);
                      }}
                      className="px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors flex items-center gap-1"
                    >
                      <FiPlay className="w-3 h-3" />
                      Open Notebook
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-gray-900 rounded-lg border border-gray-800 p-6 text-center">
              <MdCheckCircle className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400">No active notebook sessions</p>
              <p className="text-sm text-gray-500 mt-1">Launch a new notebook to get started</p>
            </div>
          )}
        </div>

        {/* Get Started Section - Now includes My Notebooks */}
        <div className="mb-8">
          <div className="bg-gray-900 rounded-xl border border-gray-800 p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <BiRocket className="w-8 h-8 text-indigo-400" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">Get Started with Notebooks</h3>
              <p className="text-gray-400 max-w-md mx-auto">
                Launch a GPU-powered Jupyter notebook to start building and testing your trading strategies
              </p>
            </div>
            
            {/* My Notebooks Section - Integrated here */}
            {userNotebooks.length > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-700">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-md font-medium text-white flex items-center gap-2">
                    <FiFolder className="w-4 h-4 text-blue-400" />
                    My Notebooks ({userNotebooks.length})
                  </h4>
                  <button
                    onClick={() => setShowUserNotebooksModal(true)}
                    className="text-sm text-indigo-400 hover:text-indigo-300"
                  >
                    View All
                  </button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {userNotebooks.slice(0, 4).map((notebook) => (
                    <button
                      key={notebook.name}
                      onClick={() => setShowNotebookLauncher(true)}
                      className="text-left p-3 bg-gray-800/50 rounded-lg border border-gray-700 hover:border-gray-600 transition-colors"
                    >
                      <FiFile className="w-4 h-4 text-gray-400 mb-1" />
                      <p className="text-sm text-white truncate" title={notebook.name}>{notebookDisplayName(notebook)}</p>
                      <p className="text-xs text-gray-500 mt-1">{formatBytes(notebook.size)}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            <div className="flex justify-center mt-6">
              <button
                onClick={() => setShowNotebookLauncher(true)}
                className="px-6 py-3 bg-white text-black font-medium rounded-lg hover:bg-gray-100 transition-colors inline-flex items-center gap-2"
              >
                <FiPlus className="w-5 h-5" />
                {userNotebooks.length > 0 ? 'Create New Notebook' : 'Launch Your First Notebook'}
              </button>
            </div>
          </div>
        </div>

        {/* Pending Template Banner */}
        {pendingTemplate && availableInstances.filter(i => i.has_notebook).length === 0 && (
          <div className="mb-8 p-4 bg-yellow-900/20 border border-yellow-800/50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                <FiZap className="w-5 h-5 text-yellow-400" />
              </div>
              <div className="flex-1">
                <p className="text-white font-medium">Template ready to open</p>
                <p className="text-sm text-gray-400">
                  Your strategy template will open automatically once your compute instance is ready.
                </p>
              </div>
              <div className="w-5 h-5 border-2 border-yellow-400/30 border-t-yellow-400 rounded-full animate-spin" />
            </div>
          </div>
        )}

        {/* Notebook Loading Error */}
        {notebookError && sampleNotebooks.length === 0 && (
          <div className="mb-8 bg-red-900/20 border border-red-800/50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <div>
                  <p className="text-white font-medium">Unable to load notebooks</p>
                  <p className="text-sm text-gray-400">Could not fetch notebook list from the server.</p>
                </div>
              </div>
              <button
                onClick={fetchNotebooks}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Sample Notebooks Section - Expanded with actual notebooks */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
            <HiOutlineSparkles className="w-5 h-5 text-yellow-500" />
            Sample Notebooks
          </h2>
          <p className="text-sm text-gray-400 mb-6">
            Ready-to-run notebooks demonstrating PropAlgos capabilities. Click any notebook to launch it on a GPU instance.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sampleNotebooks.map((notebook) => (
              <div
                key={notebook.id || notebook.name}
                className="bg-gray-900 border border-gray-800 rounded-lg p-5 hover:border-gray-700 transition-all hover:shadow-lg cursor-pointer group"
                onClick={() => {
                  setSelectedSampleNotebook(notebook);
                  if (notebook.path) sessionStorage.setItem('pending_sample_notebook', encodeNotebookPath(notebook.path));
                  setShowNotebookLauncher(true);
                }}
              >
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-gray-800 rounded-lg group-hover:bg-gray-700 transition-colors">
                    {getCategoryIcon(notebook.category)}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-white group-hover:text-indigo-400 transition-colors" title={notebook.name}>
                      {notebookDisplayName(notebook)}
                    </h3>
                    {notebook.description && (
                      <p className="text-sm text-gray-400 mt-1 line-clamp-2">
                        {notebook.description}
                      </p>
                    )}
                    <div className="flex items-center gap-3 mt-3">
                      {notebook.category && (
                        <span className="text-xs text-gray-500">
                          {notebook.category}
                        </span>
                      )}
                      <span className="text-xs text-gray-600">•</span>
                      <span className="text-xs text-gray-500">
                        {formatBytes(notebook.size)}
                      </span>
                    </div>
                  </div>
                  <FiZap className="w-4 h-4 text-indigo-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Available Compute Instances - Optional Section */}
        {availableInstances.filter(i => !i.has_notebook).length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-semibold text-white mb-4">
              Available Compute Instances
            </h2>
            <p className="text-sm text-gray-400 mb-4">
              These instances are running but don't have notebooks attached yet
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {availableInstances.filter(i => !i.has_notebook).map((instance) => (
                <div
                  key={instance.instance_id}
                  className="bg-gray-900 border border-gray-800 rounded-lg p-4 hover:border-gray-700 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-medium text-white">
                        {instance.instance_type}
                      </p>
                      <p className="text-sm text-gray-400 flex items-center gap-2 mt-1">
                        <FiCpu className="w-3 h-3" />
                        {instance.gpu_type} • {instance.region}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500 flex items-center gap-1">
                      <FiDollarSign className="w-3 h-3" />
                      {(instance.hourly_cost ?? 0).toFixed(2)}/hr
                    </span>
                    <button
                      onClick={() => {
                        setPreSelectedInstanceId(instance.instance_id);
                        setShowNotebookLauncher(true);
                      }}
                      className="px-3 py-1.5 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 transition-colors flex items-center gap-1"
                    >
                      <FiBookOpen className="w-3 h-3" />
                      Attach Notebook
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* User Notebooks Modal - Only shown when user clicks "View All" */}
      {showUserNotebooksModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-lg max-w-4xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-gray-800">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                  <FiFolder className="w-6 h-6 text-blue-400" />
                  My Notebooks
                </h2>
                <button
                  onClick={() => setShowUserNotebooksModal(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {userNotebooks.map((notebook) => (
                  <div
                    key={notebook.name}
                    className="bg-gray-800 border border-gray-700 rounded-lg p-4 hover:border-gray-600 transition-colors cursor-pointer"
                    onClick={() => {
                      setShowUserNotebooksModal(false);
                      setShowNotebookLauncher(true);
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <FiFile className="w-5 h-5 text-gray-400 mt-1 flex-shrink-0" />
                      <div className="flex-1">
                        <h3 className="font-medium text-white" title={notebook.name}>
                          {notebookDisplayName(notebook)}
                        </h3>
                        <p className="text-xs text-gray-500 mt-1">
                          Modified: {new Date(notebook.last_modified).toLocaleDateString()}
                        </p>
                      </div>
                      <span className="text-xs text-gray-500">
                        {formatBytes(notebook.size)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notebook Launcher Modal */}
      {showNotebookLauncher && (
        <NotebookLauncher
          isOpen={showNotebookLauncher}
          onClose={() => {
            setShowNotebookLauncher(false);
            setSelectedSampleNotebook(null);
            sessionStorage.removeItem('pending_sample_notebook');
          }}
          onLaunchedNewInstance={() => {
            setShowNotebookLauncher(false);
            setSelectedSampleNotebook(null);
          }}
          instances={availableInstances}
          preSelectedInstanceId={preSelectedInstanceId}
          onLaunch={launchNotebook}
          onNavigateToCompute={() => {
            setShowNotebookLauncher(false);
            navigate('/dashboard/compute');
          }}
          notebookName={selectedSampleNotebook?.name}
          notebookPath={selectedSampleNotebook?.path}
        />
      )}
    </div>
  );
}